import { useEffect, useState } from 'react';
import { useAuth } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import MemberBadge from '../components/MemberBadge';
import { LevelVerifyControl, useParticipantLevels } from '../components/LevelVerifyControl';
import { Plus, PencilSimple, Trash, Users, Calendar as CalendarIcon, XCircle } from '@phosphor-icons/react';

const WEEKDAYS = [
  { en: 'Monday',    de: 'Montag' },
  { en: 'Tuesday',   de: 'Dienstag' },
  { en: 'Wednesday', de: 'Mittwoch' },
  { en: 'Thursday',  de: 'Donnerstag' },
  { en: 'Friday',    de: 'Freitag' },
  { en: 'Saturday',  de: 'Samstag' },
  { en: 'Sunday',    de: 'Sonntag' },
];

const emptyForm = {
  title: '',
  description: '',
  weekday: 0,
  start_time: '19:00',
  duration_minutes: 60,
  price: 40,
  registration_only: false,
  max_participants: 8,
  cancellation_hours: 8,
  trainer_id: '',
  trainer_ids: [],
  start_date: '',
  end_date: '',
  is_active: true,
  weekly_recurring: true,
  weekly_end_date: '',
};

const AdminTrainingPrograms = () => {
  const { API, user, language } = useAuth();
  const L = (en, de) => (language === 'de' ? de : en);
  // Trainers can now edit + cancel group trainings (Oct 2026 policy
  // change). Admin / staff keep the full write set (create, delete, kick,
  // cash-enrol, redeem voucher). We split the capability flags accordingly:
  //   • `canModify`   → open edit dialog, cancel-program flow
  //   • `canCreate`   → "Add new program" button
  //   • `canDelete`   → hard-delete program button
  //   • `canManageParticipants` → kick / cash-enrol / voucher redeem
  const role = user?.role;
  const canModify = ['admin', 'staff', 'trainer'].includes(role);
  const canCreate = ['admin', 'staff'].includes(role) || !!user?.manages_training_programs;
  const canDelete = ['admin', 'staff'].includes(role);
  const canManageParticipants = ['admin', 'staff'].includes(role);
  // Preserve the historical `readOnly` name for participant-mutation
  // branches that previously used it, so trainer still cannot kick / redeem.
  const readOnly = !canManageParticipants;

  const [programs, setPrograms] = useState([]);
  const [trainers, setTrainers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null); // null = create, program object = edit
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [showParticipants, setShowParticipants] = useState(null);
  const [tpLevels, setTpLevels] = useParticipantLevels(API, (showParticipants?.participants || []).map(p => p.user_id));
  const [cashAddOpen, setCashAddOpen] = useState(null); // program to add cash-enrol to
  const [cashSearch, setCashSearch] = useState('');
  const [cashResults, setCashResults] = useState([]);
  const [cashChosenUser, setCashChosenUser] = useState(null);
  const [addMethod, setAddMethod] = useState('cash');
  const [userAbos, setUserAbos] = useState(null);
  const [aboId, setAboId] = useState('');
  const [aboAmount, setAboAmount] = useState('');
  useEffect(() => {
    if (!cashChosenUser || addMethod !== 'abo') { setUserAbos(null); return; }
    setAboAmount(String(cashAddOpen?.price ?? ''));
    API.get(`/subscriptions?user_id=${cashChosenUser.id}`).then(({ data }) => {
      const list = (data || []).filter(s => Number(s.balance_remaining || 0) > 0.01 && !['cancelled', 'expired', 'refunded'].includes(s.status));
      setUserAbos(list);
      setAboId(list[0]?.id || '');
    }).catch(() => setUserAbos([]));
  }, [cashChosenUser, addMethod, cashAddOpen, API]);
  const [cashSaving, setCashSaving] = useState(false);
  // Voucher-at-desk redemption dialog: { program, participant }.
  const [redeemDialog, setRedeemDialog] = useState(null);
  const [redeemVouchers, setRedeemVouchers] = useState([]);
  const [chosenRedeemVoucherId, setChosenRedeemVoucherId] = useState('');
  const [redeeming, setRedeeming] = useState(false);

  const searchCustomers = async (q) => {
    if (!q || q.length < 2) { setCashResults([]); return; }
    try {
      const { data } = await API.get(`/users/search?q=${encodeURIComponent(q)}&limit=8`);
      setCashResults((Array.isArray(data) ? data : []).slice(0, 8));
    } catch {
      setCashResults([]);
    }
  };

  const cashEnrol = async () => {
    if (!cashAddOpen || !cashChosenUser) return;
    setCashSaving(true);
    try {
      const { data } = await API.post(`/training-programs/${cashAddOpen.id}/enroll`, {
        payment_method: addMethod,
        user_id: cashChosenUser.id,
        ...(addMethod === 'abo' ? { subscription_id: aboId, abo_amount: parseFloat(aboAmount) } : {}),
      });
      toast.success(addMethod === 'abo'
        ? L(`Enrolled — ${parseFloat(aboAmount).toFixed(2)} CHF deducted from Abo`, `Angemeldet — ${parseFloat(aboAmount).toFixed(2)} CHF vom Abo abgezogen`)
        : L('Enrolled — cash logged in till', 'Angemeldet — Bar in Kasse gebucht'));
      // refresh the visible participants dialog too
      setShowParticipants(data);
      setCashAddOpen(null);
      setCashChosenUser(null);
      setCashSearch('');
      setCashResults([]);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || L('Add failed', 'Fehlgeschlagen'));
    } finally {
      setCashSaving(false);
    }
  };

  // Load the participant's active vouchers whenever the redeem dialog opens.
  useEffect(() => {
    if (!redeemDialog?.participant?.user_id) return;
    (async () => {
      try {
        const { data } = await API.get(`/vouchers/user/${redeemDialog.participant.user_id}`);
        const list = (Array.isArray(data) ? data : []).filter(v => v.is_active && Number(v.remaining_balance || v.amount || 0) >= Number(redeemDialog.participant.voucher_redemption_amount || redeemDialog.program?.price || 0));
        setRedeemVouchers(list);
        setChosenRedeemVoucherId(list[0]?.id || '');
      } catch {
        setRedeemVouchers([]);
      }
    })();
  }, [redeemDialog, API]);

  const submitRedeem = async () => {
    if (!redeemDialog?.program?.id || !redeemDialog?.participant?.user_id || !chosenRedeemVoucherId) return;
    setRedeeming(true);
    try {
      const { data } = await API.post(
        `/training-programs/${redeemDialog.program.id}/participants/${redeemDialog.participant.user_id}/redeem-voucher`,
        { voucher_id: chosenRedeemVoucherId },
      );
      toast.success(L('Voucher redeemed & logged in cash register', 'Gutschein eingelöst und in Kasse gebucht'));
      setShowParticipants(data);
      setRedeemDialog(null);
      setChosenRedeemVoucherId('');
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || L('Redeem failed', 'Einlösung fehlgeschlagen'));
    } finally {
      setRedeeming(false);
    }
  };


  const load = async () => {
    try {
      setLoading(true);
      const [{ data: progs }, { data: tr }] = await Promise.all([
        API.get('/training-programs?include_inactive=true'),
        API.get('/trainers').catch(() => ({ data: [] })),
      ]);
      setPrograms(progs || []);
      setTrainers(Array.isArray(tr) ? tr : []);
      const openId = new URLSearchParams(window.location.search).get('open');
      if (openId) {
        window.history.replaceState(null, '', window.location.pathname);
        API.get(`/training-programs/${openId}`).then(({ data }) => setShowParticipants(data)).catch(() => {});
      }
    } catch (e) {
      toast.error(L('Failed to load', 'Laden fehlgeschlagen'));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setShowForm(true);
  };
  const openEdit = (p) => {
    setEditing(p);
    setForm({
      title: p.title || '',
      description: p.description || '',
      weekday: p.weekday,
      start_time: p.start_time || '19:00',
      duration_minutes: p.duration_minutes || 60,
      price: p.price || 0,
      registration_only: !!p.registration_only,
      max_participants: p.max_participants || 8,
      cancellation_hours: p.cancellation_hours ?? 8,
      trainer_id: p.trainer_id || '',
      trainer_ids: p.trainer_ids || (p.trainer_id ? [p.trainer_id] : []),
      start_date: p.start_date || '',
      end_date: p.end_date || '',
      is_active: !!p.is_active,
      weekly_recurring: false,  // editing an existing session/program → don't regenerate
      weekly_end_date: p.weekly_end_date || '',
    });
    setShowForm(true);
  };

  const save = async () => {
    if (!form.title.trim()) {
      toast.error(L('Title required', 'Titel benötigt'));
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        weekday: Number(form.weekday),
        duration_minutes: Number(form.duration_minutes),
        price: Number(form.price),
        registration_only: !!form.registration_only,
        max_participants: Number(form.max_participants),
        cancellation_hours: Number(form.cancellation_hours ?? 8),
        trainer_id: (form.trainer_ids && form.trainer_ids[0]) || form.trainer_id || null,
        trainer_ids: form.trainer_ids || [],
        start_date: form.start_date || null,
        end_date: form.end_date || null,
        // Weekly-series generation only on CREATE, never on update.
        weekly_recurring: editing ? false : !!form.weekly_recurring,
        weekly_end_date: (!editing && form.weekly_recurring && form.weekly_end_date) ? form.weekly_end_date : null,
      };
      // Guard: when creating a weekly series we require both start_date and
      // weekly_end_date so the loop is bounded.
      if (!editing && payload.weekly_recurring) {
        if (!payload.start_date || !payload.weekly_end_date) {
          toast.error(L('Please set both start date and series end date', 'Bitte Startdatum und Serie-Enddatum setzen'));
          setSaving(false);
          return;
        }
      }
      if (editing) {
        await API.patch(`/training-programs/${editing.id}`, payload);
      } else {
        const { data } = await API.post('/training-programs', payload);
        if (data?._sessions_created > 1) {
          toast.success(L(`${data._sessions_created} weekly sessions created`, `${data._sessions_created} Wochen-Sessions erstellt`));
        }
      }
      toast.success(L('Saved', 'Gespeichert'));
      setShowForm(false);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || L('Save failed', 'Speichern fehlgeschlagen'));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (p) => {
    if (!window.confirm(L(`Delete "${p.title}"?`, `"${p.title}" wirklich löschen?`))) return;
    try {
      await API.delete(`/training-programs/${p.id}`);
      toast.success(L('Deleted', 'Gelöscht'));
      await load();
    } catch (e) {
      toast.error(L('Delete failed', 'Löschen fehlgeschlagen'));
    }
  };

  const cancelProgram = async (p) => {
    const enrolled = p.enrolled_count || 0;
    const waitlist = p.waitlist_count || 0;
    const msg = L(
      `Cancel "${p.title}" and refund ALL ${enrolled} participant(s)?\n\n• Every paying customer will get an automatic refund (card → back to card, cash/invoice → cash at reception, credits/voucher → back to balance)\n• ${waitlist} waitlisted people will be notified\n• The program will be marked as cancelled (kept for accounting)\n\nThis cannot be undone.`,
      `"${p.title}" absagen und ALLE ${enrolled} Teilnehmer refundieren?\n\n• Jeder zahlende Kunde erhält automatisch eine Rückerstattung (Karte → zurück auf die Karte, Bar/Rechnung → Bar an der Rezeption, Guthaben/Gutschein → wieder aufs Konto)\n• ${waitlist} Personen auf der Warteliste werden informiert\n• Das Programm wird als abgesagt markiert (bleibt für die Buchhaltung erhalten)\n\nDies kann nicht rückgängig gemacht werden.`
    );
    if (!window.confirm(msg)) return;
    try {
      const { data } = await API.post(`/training-programs/${p.id}/cancel-program`);
      const chf = Number(data?.total_refunded_chf || 0).toFixed(2);
      toast.success(
        L(
          `Cancelled. ${data.participants_refunded} refunded (CHF ${chf}), ${data.waitlist_notified} waitlist notified.`,
          `Abgesagt. ${data.participants_refunded} refundiert (CHF ${chf}), ${data.waitlist_notified} Warteliste informiert.`
        )
      );
      if (data.refund_errors > 0) {
        toast.warning(
          L(
            `${data.refund_errors} refund(s) failed — check the Kasse and reconcile manually.`,
            `${data.refund_errors} Rückerstattung(en) fehlgeschlagen — Kasse prüfen und manuell abstimmen.`
          )
        );
      }
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || L('Cancel failed', 'Absage fehlgeschlagen'));
    }
  };

  const kick = async (programId, userId, participant = null) => {
    // Build a clear preview of what happens on remove: session date, refund
    // amount and the refund channel — so the admin knows before pulling the
    // trigger whether the customer will get money back automatically (card /
    // credits / voucher) or whether reception has to pay cash out manually.
    const program = showParticipants || {};
    const now = new Date();
    let hoursUntil = Infinity;
    if (program.session_date) {
      const dt = new Date(`${program.session_date}T${program.start_time || '00:00'}:00`);
      hoursUntil = (dt.getTime() - now.getTime()) / 3.6e6;
    }
    const cutoffH = Number(program.cancellation_hours ?? 8);
    const insideWindow = hoursUntil < cutoffH;

    const amt = participant ? Number(participant.amount_paid || 0) : 0;
    const paidPending = participant && participant.payment_status === 'pending';
    const hasPayment = participant && amt > 0 && !paidPending;

    let msg;
    if (!participant || !hasPayment) {
      // Waitlister, unpaid, pending — nothing to refund
      msg = L(
        `Remove this person?\n\nNo refund needed (no completed payment).`,
        `Diese Person entfernen?\n\nKeine Rückerstattung nötig (keine abgeschlossene Zahlung).`
      );
    } else if (insideWindow) {
      // Inside the 8h window — server won't auto-refund
      msg = L(
        `Remove ${participant.user_name}?\n\n⚠️  Less than ${cutoffH}h before the session — NO automatic refund of ${amt.toFixed(2)} CHF. Any refund must be settled manually at reception.`,
        `${participant.user_name} entfernen?\n\n⚠️  Weniger als ${cutoffH}h vor der Session — KEINE automatische Rückerstattung von ${amt.toFixed(2)} CHF. Refund muss am Empfang manuell erfolgen.`
      );
    } else {
      // Auto-refund will happen — describe the channel
      const method = (participant.payment_method || '').toLowerCase();
      const channel = {
        card:    L('Credit card (Datatrans) — refund lands on the card in 3-5 business days',
                   'Kreditkarte (Datatrans) — Rückerstattung 3-5 Werktage auf die Karte'),
        credits: L('Account credits — instantly added back to their balance',
                   'Guthaben — sofort dem Kunden-Guthaben gutgeschrieben'),
        voucher: L('Voucher — remaining balance instantly restored',
                   'Gutschein — Restbetrag sofort zurückgebucht'),
        cash:    L('Cash — a NEGATIVE cash-register entry is logged; reception must physically pay out at the desk',
                   'Bar — negativer Kassa-Eintrag wird erstellt; Empfang muss den Betrag physisch am Tresen auszahlen'),
      }[method] || L(
        `Payment method "${method}" — refund handled manually`,
        `Zahlart "${method}" — Rückerstattung manuell abwickeln`
      );
      msg = L(
        `Remove ${participant.user_name}?\n\n💰  Refund: ${amt.toFixed(2)} CHF\n📤  Via: ${channel}`,
        `${participant.user_name} entfernen?\n\n💰  Rückerstattung: ${amt.toFixed(2)} CHF\n📤  Via: ${channel}`
      );
    }

    if (!window.confirm(msg)) return;
    try {
      const { data } = await API.post(`/training-programs/${programId}/kick`, { user_id: userId });
      setShowParticipants(data);
      await load();
      toast.success(L('Removed', 'Entfernt'));
    } catch (e) {
      toast.error(L('Kick failed', 'Entfernen fehlgeschlagen'));
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-training-programs-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-black text-slate-900">
            {L('Group Training Programs', 'Gruppen-Trainings')}
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            {L('Weekly recurring classes customers can sign up for.', 'Wöchentliche Kurse, in die sich Kunden eintragen können.')}
          </p>
        </div>
        {canCreate && (
          <Button onClick={openCreate} className="btn-primary" data-testid="tp-new-btn">
            <Plus size={16} className="mr-1" /> {L('New program', 'Neues Programm')}
          </Button>
        )}
      </div>

      {loading ? (
        <div className="text-center py-12 text-slate-400">{L('Loading…', 'Laden…')}</div>
      ) : programs.length === 0 ? (
        <div className="card-static p-12 text-center">
          <CalendarIcon size={48} className="mx-auto text-slate-300 mb-3" />
          <p className="text-slate-500">
            {L('No programs yet. Create the first one!', 'Noch keine Programme. Erstelle das erste!')}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {programs.map(p => (
            <div
              key={p.id}
              className={`card-static p-5 space-y-3 ${!p.is_active ? 'opacity-60' : ''}`}
              data-testid={`tp-card-${p.id}`}
            >
              <div className="flex items-start justify-between gap-2">
                <div>
                  <h3 className="font-bold text-slate-900">{p.title}</h3>
                  <p className="text-xs text-slate-500">
                    {p.session_date ? (
                      <>
                        <strong className="text-emerald-700">
                          {new Date(p.session_date + 'T00:00:00').toLocaleDateString(language === 'de' ? 'de-CH' : 'en-GB', { weekday: 'short', day: '2-digit', month: '2-digit', year: '2-digit' })}
                        </strong>
                        {' · '}{p.start_time} · {p.duration_minutes} min
                      </>
                    ) : (
                      <>{L(WEEKDAYS[p.weekday]?.en, WEEKDAYS[p.weekday]?.de)} · {p.start_time} · {p.duration_minutes} min</>
                    )}
                  </p>
                </div>
                <span className={`px-2 py-0.5 text-[10px] font-bold rounded-md ${p.is_active ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-200 text-slate-500'}`}>
                  {p.is_active ? L('ACTIVE', 'AKTIV') : L('INACTIVE', 'INAKTIV')}
                </span>
              </div>
              {p.description && <p className="text-sm text-slate-600 whitespace-pre-line break-words">{p.description}</p>}
              {p.trainer_name && (
                <p className="text-xs text-slate-500">
                  {L('Trainer', 'Trainer')}: <strong>{p.trainer_name}</strong>
                </p>
              )}
              <div className="flex items-center justify-between text-sm">
                <span className="font-bold text-emerald-600">{Number(p.price).toFixed(0)} CHF</span>
                <button
                  onClick={() => setShowParticipants(p)}
                  className="flex items-center gap-1 text-blue-600 hover:text-blue-800 font-semibold"
                  data-testid={`tp-participants-${p.id}`}
                >
                  <Users size={14} />
                  {p.enrolled_count}/{p.max_participants}
                  {p.waitlist_count > 0 && (
                    <span className="ml-1 text-amber-600 text-xs">+{p.waitlist_count} WL</span>
                  )}
                </button>
              </div>
              <div className="flex gap-2 pt-2 border-t border-slate-100">
                {canModify && (
                  <Button size="sm" variant="outline" onClick={() => openEdit(p)} className="flex-1" data-testid={`tp-edit-${p.id}`}>
                    <PencilSimple size={12} className="mr-1" /> {L('Edit', 'Bearbeiten')}
                  </Button>
                )}
                {canModify && (p.enrolled_count > 0 || p.waitlist_count > 0) && p.is_active !== false && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => cancelProgram(p)}
                    className="text-amber-700 border-amber-300 hover:bg-amber-50"
                    data-testid={`tp-cancel-${p.id}`}
                    title={L('Cancel training & refund all participants', 'Training absagen & alle refundieren')}
                  >
                    <XCircle size={12} className="mr-1" /> {L('Cancel & refund', 'Absagen & refundieren')}
                  </Button>
                )}
                {canDelete && (
                  <Button size="sm" variant="outline" onClick={() => remove(p)} className="text-red-600 border-red-200 hover:bg-red-50" data-testid={`tp-delete-${p.id}`}>
                    <Trash size={12} />
                  </Button>
                )}
                {!canModify && (
                  <p className="flex-1 text-[11px] italic text-slate-400 text-center py-1.5">
                    {L('View only — participants below', 'Nur Ansicht — Teilnehmer unten')}
                  </p>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create / Edit dialog */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="bg-white max-w-lg max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editing ? L('Edit program', 'Programm bearbeiten') : L('New program', 'Neues Programm')}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1">
              <Label className="label-text">{L('Title', 'Titel')} *</Label>
              <Input
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                placeholder={L('e.g. Monday Adult Training', 'z.B. Montag Erwachsenen-Training')}
                data-testid="tp-form-title"
              />
            </div>
            <div className="space-y-1">
              <Label className="label-text">{L('Description', 'Beschreibung')}</Label>
              <Textarea
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder={L('Optional', 'Optional')}
                rows={5}
                data-testid="tp-form-desc"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="label-text">{L('Weekday', 'Wochentag')}</Label>
                <Select value={String(form.weekday)} onValueChange={(v) => setForm({ ...form, weekday: Number(v) })}>
                  <SelectTrigger className="bg-slate-50 border-slate-200" data-testid="tp-form-weekday">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-white">
                    {WEEKDAYS.map((d, i) => (
                      <SelectItem key={i} value={String(i)}>{L(d.en, d.de)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="label-text">{L('Start time', 'Startzeit')}</Label>
                <Input
                  type="time"
                  value={form.start_time}
                  onChange={(e) => setForm({ ...form, start_time: e.target.value })}
                  data-testid="tp-form-time"
                />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-1">
                <Label className="label-text">{L('Duration (min)', 'Dauer (min)')}</Label>
                <Input
                  type="number" min="30" step="30"
                  value={form.duration_minutes}
                  onChange={(e) => setForm({ ...form, duration_minutes: e.target.value })}
                  data-testid="tp-form-duration"
                />
              </div>
              <div className="space-y-1">
                <Label className="label-text">{L('Price (CHF)', 'Preis (CHF)')}</Label>
                <Input
                  type="number" min="0" step="5"
                  value={form.registration_only ? 0 : form.price}
                  disabled={!!form.registration_only}
                  onChange={(e) => setForm({ ...form, price: e.target.value })}
                  data-testid="tp-form-price"
                  className={form.registration_only ? 'bg-slate-100 text-slate-400' : ''}
                />
              </div>
              <div className="space-y-1">
                <Label className="label-text">{L('Max spots', 'Max Plätze')}</Label>
                <Input
                  type="number" min="1"
                  value={form.max_participants}
                  onChange={(e) => setForm({ ...form, max_participants: e.target.value })}
                  data-testid="tp-form-max"
                />
              </div>
            </div>

            {/* Registration-only mode toggle. Free intro sessions, community
                trainings and "reservation only" flows should tick this — the
                signup then bypasses payment entirely and the price field is
                greyed out. */}
            <label className="flex items-center gap-3 py-2 cursor-pointer" data-testid="tp-form-registration-only-label">
              <input
                type="checkbox"
                checked={!!form.registration_only}
                onChange={(e) => setForm({ ...form, registration_only: e.target.checked })}
                data-testid="tp-form-registration-only"
                className="w-4 h-4 rounded border-slate-300 text-purple-600 focus:ring-purple-500"
              />
              <div className="min-w-0">
                <p className="text-sm font-semibold text-slate-800">{L('Registration only (no payment)', 'Nur Anmeldung (keine Zahlung)')}</p>
                <p className="text-[11px] text-slate-500 leading-tight">
                  {L(
                    'Users just sign up — no card / credits / voucher is charged. Ideal for free intro or community sessions.',
                    'Kunden melden sich einfach an — keine Karte / Guthaben / Gutschein wird belastet. Ideal für Gratis-Schnupperstunden oder Community-Trainings.',
                  )}
                </p>
              </div>
            </label>
            <div className="space-y-1">
              <Label className="label-text">{L('Free cancel until (hours before)', 'Gratis-Storno bis (Std. vorher)')}</Label>
              <Input
                type="number" min="0" step="1"
                value={form.cancellation_hours}
                onChange={(e) => setForm({ ...form, cancellation_hours: e.target.value })}
                data-testid="tp-form-cancellation-hours"
              />
              <p className="text-[11px] text-slate-500">
                {L('Refund is issued only when unenrol happens this many hours (or more) before the next session.', 'Rückerstattung erfolgt nur, wenn die Abmeldung so viele Stunden (oder mehr) vor dem nächsten Termin erfolgt.')}
              </p>
            </div>
            <div className="space-y-1">
              <Label className="label-text">{L('Trainers (one or more)', 'Trainer (einer oder mehrere)')}</Label>
              <div className="flex flex-wrap gap-2 p-3 border border-slate-200 rounded-lg bg-slate-50" data-testid="tp-form-trainers">
                {trainers.length === 0 && (
                  <span className="text-xs text-slate-400 italic">{L('No trainers yet', 'Noch keine Trainer')}</span>
                )}
                {trainers.map(t => {
                  const sel = (form.trainer_ids || []).includes(t.id);
                  return (
                    <button
                      key={t.id}
                      type="button"
                      onClick={() => {
                        const cur = form.trainer_ids || [];
                        const next = sel ? cur.filter(x => x !== t.id) : [...cur, t.id];
                        setForm({ ...form, trainer_ids: next, trainer_id: next[0] || '' });
                      }}
                      className={`px-3 py-1.5 rounded-full text-xs font-semibold transition ${
                        sel ? 'bg-emerald-500 text-white shadow-sm' : 'bg-white text-slate-700 border border-slate-300 hover:border-emerald-400'
                      }`}
                      data-testid={`tp-form-trainer-${t.id}`}
                    >
                      {sel && '✓ '}{t.name}
                    </button>
                  );
                })}
              </div>
              <p className="text-[11px] text-slate-500">
                {L('Tip: for 8-player classes select 2 coaches. Both will see the session blocked in their 1-on-1 schedule.',
                   'Tipp: für 8er-Klassen 2 Coaches wählen. Beide sehen die Session in ihrem 1-zu-1 Kalender als geblockt.')}
              </p>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="label-text">{L('Valid from', 'Gültig ab')}</Label>
                <Input
                  type="date"
                  value={form.start_date}
                  onChange={(e) => setForm({ ...form, start_date: e.target.value })}
                  data-testid="tp-form-start-date"
                />
              </div>
              <div className="space-y-1">
                <Label className="label-text">{L('Valid until', 'Gültig bis')}</Label>
                <Input
                  type="date"
                  value={form.end_date}
                  onChange={(e) => setForm({ ...form, end_date: e.target.value })}
                  data-testid="tp-form-end-date"
                />
              </div>
            </div>
            <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
              <Label className="text-sm font-semibold">{L('Active (visible to customers)', 'Aktiv (für Kunden sichtbar)')}</Label>
              <Switch
                checked={form.is_active}
                onCheckedChange={(v) => setForm({ ...form, is_active: v })}
                data-testid="tp-form-active"
              />
            </div>

            {!editing && (
              <div className="space-y-3 p-4 rounded-xl border-2 border-emerald-200 bg-emerald-50/40">
                <label className="flex items-start gap-2.5 cursor-pointer" data-testid="tp-form-weekly-toggle">
                  <input
                    type="checkbox"
                    className="mt-0.5 w-4 h-4 accent-emerald-600"
                    checked={!!form.weekly_recurring}
                    onChange={(e) => setForm({ ...form, weekly_recurring: e.target.checked })}
                  />
                  <span className="text-sm flex-1">
                    <span className="font-bold text-emerald-800 block">
                      {L('Auto-create weekly sessions', 'Wöchentliche Sessions automatisch erstellen')}
                    </span>
                    <span className="text-xs text-emerald-700/80">
                      {L(
                        'Creates one signup card per week between the dates below. Signup opens immediately for all.',
                        'Erstellt eine Anmelde-Karte pro Woche zwischen den Daten unten. Anmeldung öffnet sofort für alle.',
                      )}
                    </span>
                  </span>
                </label>
                {form.weekly_recurring && (
                  <div className="grid grid-cols-2 gap-3 pl-6">
                    <div className="space-y-1">
                      <Label className="label-text text-emerald-900">{L('First session date', 'Erstes Session-Datum')}</Label>
                      <Input
                        type="date"
                        value={form.start_date}
                        onChange={(e) => setForm({ ...form, start_date: e.target.value })}
                        className="bg-white border-emerald-200"
                        data-testid="tp-form-weekly-start"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="label-text text-emerald-900">{L('Series ends on', 'Serie endet am')}</Label>
                      <Input
                        type="date"
                        value={form.weekly_end_date}
                        onChange={(e) => setForm({ ...form, weekly_end_date: e.target.value })}
                        min={form.start_date || undefined}
                        className="bg-white border-emerald-200"
                        data-testid="tp-form-weekly-end"
                      />
                    </div>
                  </div>
                )}
              </div>
            )}
            <div className="flex gap-2 pt-2">
              <Button variant="outline" onClick={() => setShowForm(false)} className="flex-1">
                {L('Cancel', 'Abbrechen')}
              </Button>
              <Button onClick={save} disabled={saving} className="btn-primary flex-1" data-testid="tp-form-save">
                {saving ? L('Saving…', 'Speichern…') : L('Save', 'Speichern')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Participants + Waitlist dialog */}
      <Dialog open={!!showParticipants} onOpenChange={(o) => !o && setShowParticipants(null)}>
        <DialogContent className="bg-white max-w-lg max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{showParticipants?.title}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {/* Enrolled section */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm font-bold text-slate-700 uppercase tracking-wider">
                  {L('Enrolled', 'Angemeldet')} · {(showParticipants?.participants || []).length}/{showParticipants?.max_participants}
                </p>
                {!readOnly && (
                  <Button
                    size="sm"
                    onClick={() => setCashAddOpen(showParticipants)}
                    className="btn-primary"
                    data-testid="tp-cash-add-btn"
                  >
                    <Plus size={12} className="mr-1" />
                    {L('Add (cash)', 'Hinzufügen (Bar)')}
                  </Button>
                )}
              </div>
              {(showParticipants?.participants || []).length === 0 ? (
                <p className="text-sm text-slate-400 italic py-4 text-center">
                  {L('No enrolments yet.', 'Noch keine Anmeldungen.')}
                </p>
              ) : (
                <ul className="space-y-1">
                  {(showParticipants?.participants || []).map((pt, i) => (
                    <li key={i} className={`flex items-center justify-between px-3 py-2 rounded-md ${pt.voucher_redemption_pending ? 'bg-fuchsia-50 border border-fuchsia-200' : 'bg-slate-50'}`} data-testid={`tp-participant-row-${i}`}>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-slate-900 flex items-center gap-2 flex-wrap">
                          {pt.user_name}
                          <MemberBadge user={{ user_id: pt.user_id, is_member: tpLevels[pt.user_id]?.is_member }} size={13} />
                          <LevelVerifyControl userId={pt.user_id} info={tpLevels[pt.user_id]} API={API} L={L}
                            onChanged={(v) => setTpLevels(m => ({ ...m, [pt.user_id]: { ...(m[pt.user_id] || {}), level_verified: v } }))} />
                          {pt.payment_status === 'pending' && (
                            <span className="text-[9px] bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded font-bold uppercase">
                              {L('Pending', 'Ausstehend')}
                            </span>
                          )}
                          {pt.voucher_redemption_pending && (
                            <span className="text-[9px] bg-fuchsia-500 text-white px-1.5 py-0.5 rounded font-bold uppercase flex items-center gap-1" data-testid={`tp-voucher-pending-${i}`} title={L('Redeem voucher at reception', 'Gutschein am Empfang einlösen')}>
                              🎟️ {L('Voucher pending', 'Gutschein offen')}
                            </span>
                          )}
                        </p>
                        <p className="text-[11px] text-slate-500">
                          {pt.user_email} · <span className="uppercase font-semibold">{pt.payment_method || '?'}</span>
                          {pt.amount_paid ? <> · {Number(pt.amount_paid).toFixed(2)} CHF</> : null}
                          {pt.voucher_redemption_pending && <> · <span className="text-fuchsia-700 font-semibold">{Number(pt.voucher_redemption_amount || showParticipants?.price || 0).toFixed(2)} CHF</span></>}
                        </p>
                      </div>
                      <div className="flex items-center gap-1">
                        {!readOnly && pt.voucher_redemption_pending && (
                          <button
                            onClick={() => setRedeemDialog({ program: showParticipants, participant: pt })}
                            className="px-2 py-1 text-[11px] font-bold uppercase bg-fuchsia-600 text-white hover:bg-fuchsia-700 rounded whitespace-nowrap"
                            data-testid={`tp-redeem-voucher-${i}`}
                          >
                            {L('Redeem', 'Einlösen')}
                          </button>
                        )}
                        {!readOnly && (
                          <button
                            onClick={() => kick(showParticipants.id, pt.user_id, pt)}
                            className="p-1.5 text-red-500 hover:bg-red-50 rounded"
                            title={L('Remove', 'Entfernen')}
                            data-testid={`tp-kick-${i}`}
                          >
                            <Trash size={14} />
                          </button>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {/* Waitlist section */}
            <div>
              <p className="text-sm font-bold text-slate-700 uppercase tracking-wider mb-2">
                {L('Waitlist', 'Warteliste')} · {(showParticipants?.waitlist || []).length}
              </p>
              {(showParticipants?.waitlist || []).length === 0 ? (
                <p className="text-xs text-slate-400 italic py-2 text-center">
                  {L('Nobody waiting.', 'Niemand auf der Warteliste.')}
                </p>
              ) : (
                <ul className="space-y-1">
                  {(showParticipants?.waitlist || []).map((w, i) => (
                    <li key={i} className={`flex items-center justify-between px-3 py-2 rounded-md ${w.status === 'offered' ? 'bg-amber-50 border border-amber-200' : 'bg-slate-50'}`} data-testid={`tp-waitlist-row-${i}`}>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-slate-900 flex items-center gap-2">
                          {w.user_name}
                          {w.status === 'offered' && (
                            <span className="text-[9px] bg-amber-500 text-white px-1.5 py-0.5 rounded font-bold uppercase">
                              {L('Offered', 'Angeboten')}
                            </span>
                          )}
                        </p>
                        <p className="text-[11px] text-slate-500">
                          {w.user_email}
                          {w.offer_expires_at && (
                            <> · {L('expires', 'läuft ab')}: {new Date(w.offer_expires_at).toLocaleString(language === 'de' ? 'de-CH' : 'en-GB', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}</>
                          )}
                        </p>
                      </div>
                      {!readOnly && (
                        <button
                          onClick={() => kick(showParticipants.id, w.user_id)}
                          className="p-1.5 text-red-500 hover:bg-red-50 rounded"
                          title={L('Remove', 'Entfernen')}
                          data-testid={`tp-kick-wl-${i}`}
                        >
                          <Trash size={14} />
                        </button>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Cash-enrol dialog */}
      <Dialog open={!!cashAddOpen} onOpenChange={(o) => !o && setCashAddOpen(null)}>
        <DialogContent className="bg-white max-w-md">
          <DialogHeader>
            <DialogTitle>{L('Add customer', 'Kunde hinzufügen')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-2" data-testid="tp-add-method">
              {[['cash', L('Cash at reception', 'Bar an der Rezeption')], ['abo', L('From Abo', 'Vom Abo abziehen')]].map(([k, lbl]) => (
                <button key={k} type="button" onClick={() => setAddMethod(k)} data-testid={`tp-add-method-${k}`}
                  className={`py-2 rounded-lg border-2 text-sm font-bold ${addMethod === k ? 'bg-emerald-500 border-emerald-600 text-white' : 'bg-white border-slate-200 text-slate-700'}`}>{lbl}</button>
              ))}
            </div>
            <p className="text-sm text-slate-600">
              {addMethod === 'abo'
                ? L(`The chosen amount is deducted from the customer's Abo for "${cashAddOpen?.title}" (no Kasse entry — the Abo was booked when sold).`,
                    `Der gewählte Betrag wird für "${cashAddOpen?.title}" vom Abo des Kunden abgezogen (kein Kasseneintrag — das Abo wurde beim Verkauf verbucht).`)
                : L(
                `The full price of ${cashAddOpen?.price} CHF will be logged in the till as cash payment for "${cashAddOpen?.title}".`,
                `Der volle Preis von ${cashAddOpen?.price} CHF wird als Barzahlung für "${cashAddOpen?.title}" in der Kasse gebucht.`,
              )}
            </p>
            <div className="space-y-1">
              <Label className="label-text">{L('Search customer', 'Kunde suchen')}</Label>
              <Input
                value={cashSearch}
                onChange={(e) => { setCashSearch(e.target.value); searchCustomers(e.target.value); }}
                placeholder={L('Type name or email…', 'Name oder E-Mail…')}
                data-testid="tp-cash-search"
              />
              {cashResults.length > 0 && (
                <ul className="max-h-40 overflow-y-auto border border-slate-200 rounded-md divide-y">
                  {cashResults.map((u) => (
                    <li
                      key={u.id}
                      onClick={() => { setCashChosenUser(u); setCashSearch(u.name); setCashResults([]); }}
                      className="px-3 py-2 hover:bg-emerald-50 cursor-pointer text-sm"
                      data-testid={`tp-cash-result-${u.id}`}
                    >
                      <div className="font-semibold text-slate-900">{u.name}</div>
                      <div className="text-[11px] text-slate-500">{u.email}</div>
                    </li>
                  ))}
                </ul>
              )}
              {cashChosenUser && (
                <div className="mt-2 p-2 bg-emerald-50 border border-emerald-200 rounded-md text-sm">
                  <strong>{cashChosenUser.name}</strong> · {cashChosenUser.email}
                </div>
              )}
              {cashChosenUser && addMethod === 'abo' && (
                <div className="mt-2 space-y-2" data-testid="tp-abo-panel">
                  {userAbos === null ? <p className="text-xs text-slate-500">…</p> : userAbos.length === 0 ? (
                    <p className="text-xs text-red-600" data-testid="tp-abo-none">{L('No active Abo with balance for this customer.', 'Kein aktives Abo mit Guthaben für diesen Kunden.')}</p>
                  ) : (
                    <>
                      <select value={aboId} onChange={(e) => setAboId(e.target.value)} className="w-full h-9 rounded-md border border-slate-200 bg-white px-2 text-sm" data-testid="tp-abo-select">
                        {userAbos.map(s => <option key={s.id} value={s.id}>Abo {s.type || ''} · {s.used_lessons || 0}/{s.total_lessons || 0} · {Number(s.balance_remaining || 0).toFixed(2)} CHF</option>)}
                      </select>
                      <div className="flex items-center gap-2">
                        <Label className="label-text whitespace-nowrap">{L('Deduct', 'Abziehen')}</Label>
                        <Input type="number" step="0.05" min="0" value={aboAmount} onChange={(e) => setAboAmount(e.target.value)} className="h-9" data-testid="tp-abo-amount" />
                        <span className="text-sm text-slate-600">CHF</span>
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
            <div className="flex gap-2 pt-2">
              <Button variant="outline" onClick={() => { setCashAddOpen(null); setCashChosenUser(null); setCashSearch(''); }} className="flex-1">
                {L('Cancel', 'Abbrechen')}
              </Button>
              <Button
                onClick={cashEnrol}
                disabled={!cashChosenUser || cashSaving || (addMethod === 'abo' && (!aboId || !(parseFloat(aboAmount) > 0)))}
                className="btn-primary flex-1"
                data-testid="tp-cash-confirm"
              >
                {cashSaving ? L('Adding…', 'Hinzufügen…') : addMethod === 'abo' ? L('Add & deduct from Abo', 'Hinzufügen & vom Abo abziehen') : L('Add & book cash', 'Hinzufügen & bar buchen')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Voucher-at-desk redemption dialog */}
      <Dialog open={!!redeemDialog} onOpenChange={(o) => { if (!o) { setRedeemDialog(null); setChosenRedeemVoucherId(''); } }}>
        <DialogContent className="bg-white border-fuchsia-200 text-slate-900 max-w-md" data-testid="tp-redeem-dialog">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold flex items-center gap-2">
              🎟️ {L('Redeem voucher at desk', 'Gutschein am Empfang einlösen')}
            </DialogTitle>
            <DialogDescription>
              {redeemDialog?.participant?.user_name} · {redeemDialog?.program?.title} · {Number(redeemDialog?.participant?.voucher_redemption_amount || redeemDialog?.program?.price || 0).toFixed(2)} CHF
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            {redeemVouchers.length === 0 ? (
              <div className="rounded-lg bg-red-50 border border-red-200 p-3 text-sm text-red-800">
                {L('No active voucher with sufficient balance found for this user. Kick them and pay in cash instead.',
                   'Kein aktiver Gutschein mit ausreichendem Guthaben gefunden. Bitte manuell entfernen und bar bezahlen.')}
              </div>
            ) : (
              <div className="space-y-2">
                <p className="text-xs text-slate-500 uppercase font-bold tracking-wide">{L('Pick voucher', 'Gutschein wählen')}</p>
                <div className="space-y-1.5">
                  {redeemVouchers.map((v) => (
                    <label key={v.id} className={`flex items-center gap-3 rounded-lg border-2 p-2.5 cursor-pointer transition ${chosenRedeemVoucherId === v.id ? 'border-fuchsia-500 bg-fuchsia-50' : 'border-slate-200 hover:border-slate-300'}`}>
                      <input
                        type="radio"
                        name="redeem-voucher"
                        value={v.id}
                        checked={chosenRedeemVoucherId === v.id}
                        onChange={() => setChosenRedeemVoucherId(v.id)}
                        data-testid={`tp-redeem-voucher-option-${v.code}`}
                      />
                      <div className="flex-1">
                        <div className="text-sm font-bold text-slate-900">{v.code}</div>
                        <div className="text-[11px] text-slate-500">{L('Balance', 'Guthaben')}: <span className="font-semibold text-emerald-700">{Number(v.remaining_balance || v.amount || 0).toFixed(2)} CHF</span></div>
                      </div>
                    </label>
                  ))}
                </div>
              </div>
            )}
          </div>
          <div className="flex gap-2 pt-2">
            <Button variant="outline" onClick={() => { setRedeemDialog(null); setChosenRedeemVoucherId(''); }} className="flex-1" disabled={redeeming}>
              {L('Cancel', 'Abbrechen')}
            </Button>
            <Button
              onClick={submitRedeem}
              disabled={!chosenRedeemVoucherId || redeeming}
              className="flex-1 bg-fuchsia-600 hover:bg-fuchsia-700 text-white font-bold"
              data-testid="tp-redeem-confirm"
            >
              {redeeming ? L('Redeeming…', 'Einlösen…') : L('Redeem voucher', 'Gutschein einlösen')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminTrainingPrograms;
